import React from 'react';

interface NotificationProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'save' | 'publish' | 'unpublish' | 'republish' | 'tags' | 'error' | 'password-reset' | 
          'profanity' | 'moderation' | 'signin-required' | 'image-too-large' | 'profile-updated' | 
          'privacy-updated' | 'feedback-received' | 'story-returned' | 'story-submitted' | 
          'assignment-created' | 'class-joined'| 'feedback-saved'| 'assignment-saved' | 'assignment-deleted'  | 'code-regenerated';    
    storyTitle?: string;
    wordCount?: number;
    totalWords?: number;
    errorMessage?: string;
    onAction?: () => void;
    customMessage?: string;
    teacherName?: string;
    className?: string;
    studentName?: string;
    assignmentTitle?: string;
}

const Notification: React.FC<NotificationProps> = ({ 
    isOpen, 
    onClose, 
    type,
    storyTitle,
    wordCount,
    totalWords,
    errorMessage,
    onAction,
    customMessage,
    teacherName,
    className,
    studentName,
    assignmentTitle
}) => {
   
    if (!isOpen) return null;

    const getContent = () => {
        switch(type) {
            case 'save':
                const getMessage = () => {
                    if (customMessage) {
                      return {
                        emoji: '✨',
                        title: 'Success!',
                        message: customMessage,
                        subtitle: '',
                        buttonText: 'Got it! 👍',
                        background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                        successColor: '#2e7d32'
                      };
                    }
                    
                    const words = totalWords || 0;
        
                    if (totalWords === undefined || totalWords === null) {
                        return {
                            emoji: '✅',
                            title: 'Saved!',
                            message: "Your changes have been saved.",
                            subtitle: '',
                            buttonText: 'Got it! 👍',
                            background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                            successColor: '#2e7d32'
                        };
                    }

                    if (words < 100) {
                        return {
                            emoji: '🌱',
                            title: 'Great Start!',
                            message: "Every story begins with a single word. Keep going!",
                            subtitle: `You've written ${words} words so far.`,
                            buttonText: 'Keep Writing! ✍️',
                            background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                            successColor: '#2e7d32'
                        };
                    } else if (words < 500) {
                        return {
                            emoji: '✨',
                            title: 'You\'re on a Roll!',
                            message: "Your story is taking shape beautifully!",
                            subtitle: `${words} words and counting!`,
                            buttonText: 'Keep Writing! ✍️',
                            background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                            successColor: '#2e7d32'
                        };
                    } else if (words < 1000) {
                        return {
                            emoji: '🚀',
                            title: 'Amazing Progress!',
                            message: "You're building something awesome!",
                            subtitle: `${words} words - that's incredible!`,
                            buttonText: 'Keep Writing! ✍️',
                            background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                            successColor: '#2e7d32'
                        };
                    } else if (words < 5000) {
                        return {
                            emoji: '🌟',
                            title: 'Wow, Look at You!',
                            message: "You're a writing machine!",
                            subtitle: `${words.toLocaleString()} words! Keep it up!`,
                            buttonText: 'Keep Writing! ✍️',
                            background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                            successColor: '#2e7d32'
                        };
                    } else {
                        return {
                            emoji: '🏆',
                            title: 'You\'re a True Author!',
                            message: "This is a real book now!",
                            subtitle: `${words.toLocaleString()} words - that's amazing!`,
                            buttonText: 'Keep Writing! ✍️',
                            background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                            successColor: '#2e7d32'
                        };
                    }
                };
                return getMessage();

            case 'publish':
                return {
                    emoji: '🎉',
                    title: 'Story Published!',
                    message: `"${storyTitle}" is now live!`,
                    subtitle: 'Other kids can read it on Featured Stories.',
                    buttonText: 'Awesome! 🎊',
                    background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                    successColor: '#2e7d32'
                };

            case 'unpublish':
                return {
                    emoji: '📦',
                    title: 'Story Unpublished',
                    message: `"${storyTitle}" is now private.`,
                    subtitle: 'You can republish it anytime!',
                    buttonText: 'Got it! 👍',
                    background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                    successColor: '#F57C00'
                };

            case 'republish':
                return {
                    emoji: '✨',
                    title: 'Story Republished!',
                    message: `"${storyTitle}" is live again!`,
                    subtitle: 'Your story is back on Featured Stories.',
                    buttonText: 'Awesome! 🎉',
                    background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                    successColor: '#2e7d32'
                };

            case 'tags':
                return {
                    emoji: '🏷️',
                    title: 'Tags Updated!',
                    message: 'Your story tags have been saved.',
                    subtitle: 'This helps other kids find your story!',
                    buttonText: 'Cool! 👍',
                    background: 'linear-gradient(135deg, #F3E5F5, #E1BEE7)',
                    successColor: '#7B1FA2'
                };

            case 'password-reset':
                return {
                    emoji: '📧',
                    title: 'Password Reset Email Sent!',
                    message: storyTitle ? `Sent to: ${storyTitle}` : 'Check your email inbox for the reset link.',
                    subtitle: '⚠️ Ask your parent or guardian to check email (and spam folder) for the reset link!',
                    buttonText: 'Got it! 👍',
                    background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)',
                    successColor: '#1976D2'
                };

            case 'profanity':
                return {
                    emoji: '✏️',
                    title: 'Story Needs Editing',
                    message: errorMessage 
                        ? `Please change or remove these words: "${errorMessage}"`
                        : 'Your story contains words that need to be changed before publishing.',
                    subtitle: 'These words aren\'t allowed on Scriblish. Replace them with different words and try again!',
                    buttonText: 'I\'ll Fix It! 👍',
                    background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                    successColor: '#F57C00'
                };

            case 'signin-required':
                return {
                    emoji: '💜',
                    title: 'Sign In to Like Stories!',
                    message: 'Create a free account to like stories and support other authors.',
                    subtitle: 'Join the Scriblish community today!',
                    buttonText: 'Sign In / Sign Up',
                    background: 'linear-gradient(135deg, #F3F0FF, #E9E4F7)',
                    successColor: '#6C5CE7'
                };

            case 'profile-updated':
                return {
                    emoji: '✨',
                    title: 'Profile Updated!',
                    message: 'Your profile has been saved successfully.',
                    subtitle: 'All your published stories have been updated too!',
                    buttonText: 'Awesome! 👍',
                    background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                    successColor: '#2e7d32'
                };
        
            case 'privacy-updated':
                return {
                    emoji: '🔒',
                    title: 'Privacy Settings Saved!',
                    message: 'Your privacy preferences have been updated.',
                    subtitle: 'Your settings are now active.',
                    buttonText: 'Got it! 👍',
                    background: 'linear-gradient(135deg, #F3E5F5, #E1BEE7)',
                    successColor: '#7B1FA2'
                };

            case 'image-too-large':
                return {
                    emoji: '📸',
                    title: 'Image Too Large!',
                    message: 'Please use images smaller than 200KB.',
                    subtitle: '💡 Tip: Try using pre-made graphics from the library, or compress your image first!',
                    buttonText: 'Got It! 👍',
                    background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                    successColor: '#F57C00'
                };

            case 'error':
                const formattedMessage = errorMessage?.includes('\n') 
                    ? errorMessage.split('\n').map((line, i) => (
                        <div key={i} style={{ 
                            marginBottom: line.trim() === '' ? '8px' : '4px',
                            textAlign: line.match(/^\d+\./) ? 'left' : 'center'
                        }}>
                            {line}
                        </div>
                      ))
                    : errorMessage;
                
                return {
                    emoji: '😕',
                    title: 'Oops!',
                    message: formattedMessage || 'Something went wrong.',
                    subtitle: '',
                    buttonText: 'Okay',
                    background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                    successColor: '#F57C00'
                };

            case 'moderation':
                return {
                    emoji: '🛡️',
                    title: 'Safety Check',
                    message: errorMessage || 'Some content needs review.',
                    subtitle: 'Please update these sections and try publishing again.',
                    buttonText: 'I\'ll Fix It! 👍',
                    background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                    successColor: '#F57C00'
                };

            // NEW CLASSROOM NOTIFICATIONS
            case 'feedback-received':
                return {
                    emoji: '💬',
                    title: 'New Feedback!',
                    message: teacherName 
                        ? `${teacherName} left feedback on "${storyTitle}"`
                        : `You have new feedback on "${storyTitle}"`,
                    subtitle: 'Click to view their comments and suggestions.',
                    buttonText: 'View Feedback 👀',
                    background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)',
                    successColor: '#1976D2'
                };

                case 'feedback-saved':
  return {
    emoji: '💾',
    title: 'Feedback Saved!',
    message: 'Your comments have been saved.',
    subtitle: 'Students can now see your feedback.',
    buttonText: 'Done ✓',
    background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)',
    successColor: '#1976D2'
  };

            case 'story-returned':
                return {
                    emoji: '🔄',
                    title: 'Story Needs Revision',
                    message: teacherName 
                        ? `${teacherName} returned "${storyTitle}" for revision`
                        : `"${storyTitle}" needs revision`,
                    subtitle: 'Check the feedback and make changes, then resubmit!',
                    buttonText: 'View & Edit 📝',
                    background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                    successColor: '#F57C00'
                };

            case 'story-submitted':
                return {
                    emoji: '✅',
                    title: 'Story Submitted!',
                    message: className 
                        ? `"${storyTitle}" submitted to ${className}`
                        : `"${storyTitle}" submitted successfully`,
                    subtitle: 'Your teacher will review it soon!',
                    buttonText: 'Great! 🎉',
                    background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                    successColor: '#2e7d32'
                };

                case 'assignment-saved':
                    return {
                      emoji: '💾',
                      title: 'Assignment Saved!',
                      message: 'Your assignment has been saved.',
                      subtitle: 'Students can now see your assignment.',
                      buttonText: 'Done ✓',
                      background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)',
                      successColor: '#1976D2'
                    };

            case 'assignment-created':
                return {
                    emoji: '📝',
                    title: 'New Assignment!',
                    message: className 
                        ? `New assignment in ${className}`
                        : 'You have a new writing assignment',
                    subtitle: customMessage || 'Check your dashboard to get started.',
                    buttonText: 'View Assignment 📚',
                    background: 'linear-gradient(135deg, #F3E5F5, #E1BEE7)',
                    successColor: '#7B1FA2'
                };

                case 'assignment-deleted':
                    return {
                        emoji: '🗑️',
                        title: 'Assignment Deleted',
                        message: assignmentTitle 
                            ? `"${assignmentTitle}" has been deleted`
                            : 'Assignment deleted successfully',
                        subtitle: 'Students will no longer see this assignment.',
                        buttonText: 'Got it 👍',
                        background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                        successColor: '#F57C00'
                    };

            case 'class-joined':
                return {
                    emoji: '🎓',
                    title: 'Welcome to Class!',
                    message: className 
                        ? `You've joined ${className}`
                        : 'Successfully joined the class',
                    subtitle: teacherName ? `Taught by ${teacherName}` : 'Start writing your first story!',
                    buttonText: 'Let\'s Go! 🚀',
                    background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                    successColor: '#2e7d32'
                };

            default:
                return {
                    emoji: '✨',
                    title: 'Success!',
                    message: 'Action completed.',
                    subtitle: '',
                    buttonText: 'Okay',
                    background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                    successColor: '#2e7d32'
                };
        }
    };
    

    const content = getContent();

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px',
                animation: 'fadeIn 0.2s ease',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            }}
            onClick={onClose}
        >
            <div 
                style={{
                    background: 'white',
                    borderRadius: '24px',
                    padding: '40px',
                    maxWidth: '450px',
                    width: '100%',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    border: '3px solid #6C5CE7',
                    position: 'relative',
                    animation: 'slideUp 0.3s ease'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#999',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f0f0';
                        e.currentTarget.style.color = '#333';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#999';
                    }}
                >
                    ×
                </button>

                {/* Content */}
                <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                        fontSize: '4rem', 
                        marginBottom: '20px',
                        animation: 'bounce 0.5s ease'
                    }}>
                        {content.emoji}
                    </div>
                    
                    <h2 style={{
                        fontSize: '1.8rem',
                        fontWeight: '800',
                        color: '#1a3a52',
                        marginBottom: '15px',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                    }}>
                        {content.title}
                    </h2>
                    
                    <p style={{
                        fontSize: '1.1rem',
                        color: '#666',
                        lineHeight: '1.6',
                        marginBottom: '10px',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        fontWeight: '500'
                    }}>
                        {content.message}
                    </p>

                    {content.subtitle && (
                        <p style={{
                            fontSize: '0.95rem',
                            color: '#6C5CE7',
                            fontWeight: '700',
                            marginBottom: '25px',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                        }}>
                            {content.subtitle}
                        </p>
                    )}

                    {/* Status box - only show for success types */}
                    {type !== 'error' && type !== 'signin-required' && type !== 'profanity' && (
                        <div style={{
                            background: content.background,
                            borderRadius: '16px',
                            padding: '20px',
                            marginBottom: '25px'
                        }}>
                            <div style={{
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                color: content.successColor,
                                marginBottom: '8px',
                                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                            }}>
                                ✓ {type === 'save' ? 'Story Saved Successfully!' :
                                   type === 'publish' ? 'Published!' :
                                   type === 'unpublish' ? 'Unpublished!' :
                                   type === 'republish' ? 'Republished!' :
                                   type === 'tags' ? 'Tags Saved!' :
                                   type === 'password-reset' ? 'Email Sent!' :
                                   'Complete!'}
                            </div>
                            <div style={{
                                fontSize: '0.9rem',
                                color: content.successColor,
                                opacity: 0.8,
                                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                                fontWeight: '500'
                            }}>
                                {type === 'save' ? 'Your work is safe and sound' :
                                 'All changes saved'}
                            </div>
                        </div>
                    )}

                    {/* Action button */}
                    <button
                        onClick={() => {
                            if (onAction) {
                                onAction();
                            }
                            onClose();
                        }}
                        style={{
                            width: '100%',
                            padding: '16px',
                            background: '#6C5CE7',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            boxShadow: '0 4px 15px rgba(108, 92, 231, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.background = '#5B4BC4';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(108, 92, 231, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.background = '#6C5CE7';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(108, 92, 231, 0.3)';
                        }}
                    >
                        {content.buttonText}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes slideUp {
                    from { 
                        transform: translateY(20px);
                        opacity: 0;
                    }
                    to { 
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
            `}</style>
        </div>
    );
};

export default Notification;










